import { ReactElement, MouseEvent, useCallback } from 'react';
import {
  Box,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { AttachmentsScope } from 'shared/domain/report/types/model/common';
import {
  attachmentsScopeLabelKey,
  attachmentsScopeTooltipLabelKey,
  formatTranslation,
} from 'intl';
import { ReportTranslationKeys } from 'intl/keys';
import {
  getReportSegmentedControlWrapperSx,
  REPORT_SEGMENTED_CONTROL_GROUP_SX,
  REPORT_SEGMENTED_CONTROL_LABEL_SX,
} from '../../reportSegmentedControlStyles';

const SCOPE_ORDER: AttachmentsScope[] = [
  AttachmentsScope.all,
  AttachmentsScope.main,
];

export function AttachmentsScopeSegmentedControl({
  value,
  onChange,
  disabled,
  'data-qa': dataQa,
}: {
  value: AttachmentsScope;
  onChange: (scope: AttachmentsScope) => void;
  disabled?: boolean;
  'data-qa'?: string;
}): ReactElement {
  const intl = useIntl();

  const handleChange = useCallback(
    (_: MouseEvent<HTMLElement>, next: AttachmentsScope | null) => {
      if (next !== null && !disabled) {
        onChange(next);
      }
    },
    [disabled, onChange]
  );

  return (
    <Box
      aria-disabled={disabled || undefined}
      sx={getReportSegmentedControlWrapperSx(disabled)}
      data-qa={dataQa}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        size='small'
        aria-label={formatTranslation(intl, ReportTranslationKeys.suboptionAttachmentsScope,)}
        sx={REPORT_SEGMENTED_CONTROL_GROUP_SX}
      >
        {SCOPE_ORDER.map((scope) => (
          <MuiToggleButton
            key={scope}
            value={scope}
            data-qa={dataQa ? `${dataQa}_${scope}` : undefined}
          >
            <Tooltip
              title={formatTranslation(
                intl,
                attachmentsScopeTooltipLabelKey(scope)
              )}
              arrow
              placement='top'
            >
              <Box component='span' sx={REPORT_SEGMENTED_CONTROL_LABEL_SX}>
                {formatTranslation(intl, attachmentsScopeLabelKey(scope))}
              </Box>
            </Tooltip>
          </MuiToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
